import * as PopperJS from "@popperjs/core";
import { CSSProperties, useState } from "react";
import { usePopper } from "react-popper";

import { TooltipPlacement } from "./tooltip.types";

const useShowHideState = (
  update: (() => Promise<Partial<PopperJS.State>>) | null,
  defaultValue = false
) => {
  const [shouldShow, setShouldShow] = useState(defaultValue);
  const show = () => {
    // update position before render to fix placement bugs when Popper cannot
    // determine the bounding rectangle of the reference element on first render
    update && update();
    setShouldShow(true);
  };
  const hide = () => setShouldShow(false);

  return {
    shouldShow,
    show,
    hide,
  };
};

const useElementReference = <
  ElementType extends HTMLElement = HTMLElement
>() => {
  return useState<ElementType | null>(null);
};

type PopperStyles = {
  arrow: CSSProperties;
  popper: CSSProperties;
};

type PopperAttributes = {
  popper: ReturnType<typeof usePopper>["attributes"]["popper"];
};

export type usePopperParameters = Parameters<typeof usePopper>;

type usePopperWithPlacementArgs = {
  referenceElement: usePopperParameters[0];
  popperElement: usePopperParameters[1];
  options: usePopperParameters[2];
  defaultPlacement: TooltipPlacement;
};

const usePopperWithPlacement = ({
  referenceElement,
  popperElement,
  options,
  defaultPlacement,
}: usePopperWithPlacementArgs) => {
  const { update, styles, attributes } = usePopper(
    referenceElement,
    popperElement,
    options
  );

  const placementFromPopper = attributes.popper?.["data-popper-placement"] as
    | TooltipPlacement
    | undefined;

  const placement = placementFromPopper ?? defaultPlacement;

  return {
    update,
    styles: styles as PopperStyles,
    attributes: attributes as PopperAttributes,
    placement,
  };
};

type useTooltipArgs = {
  defaultPlacement: TooltipPlacement;
  popperOptions: usePopperParameters[2];
};

export const useTooltip = ({
  defaultPlacement,
  popperOptions,
}: useTooltipArgs) => {
  const [referenceElement, setReferenceElement] = useElementReference();
  const [popperElement, setPopperElement] = useElementReference();
  const [arrowElement, setArrowElement] = useElementReference();

  const { update, styles, attributes, placement } = usePopperWithPlacement({
    referenceElement,
    popperElement,
    defaultPlacement,
    options: {
      modifiers: [
        {
          name: "arrow",
          options: {
            element: arrowElement,
            padding: 20,
          },
        },
        { name: "flip" },
      ],
      placement: defaultPlacement,
      ...popperOptions,
    },
  });

  const {
    shouldShow: shouldShowTooltip,
    show: showTooltip,
    hide: hideTooltip,
  } = useShowHideState(update);

  return {
    arrowStyles: styles.arrow,
    tooltipAttributes: attributes.popper,
    tooltipStyles: styles.popper,
    placement,
    setReferenceElement,
    setTooltipElement: setPopperElement,
    setArrowElement,
    shouldShowTooltip,
    showTooltip,
    hideTooltip,
  };
};
